import { useQuery } from '@tanstack/react-query';
import { fetchHealth } from '../../api/client';
import { t, type MessageKey } from '../../i18n';

const CHECK_LABELS: Record<string, MessageKey> = {
  database: 'status.database',
  redis: 'status.redis',
};

export function SystemStatus() {
  const { data, error, isPending, refetch, isFetching } = useQuery({
    queryKey: ['health', 'ready'],
    queryFn: ({ signal }) => fetchHealth(signal),
    refetchInterval: 30_000,
  });

  return (
    <section
      aria-labelledby="system-status-title"
      className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 id="system-status-title" className="text-base font-semibold">
        {t('status.title')}
      </h2>

      <div aria-live="polite" className="mt-3">
        {isPending && <p className="text-sm text-slate-600">{t('status.loading')}</p>}

        {error && (
          <div role="alert" className="flex items-center gap-3 text-sm text-red-700">
            <span>{t('status.error')}</span>
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="rounded border border-red-300 px-2 py-1 font-medium hover:bg-red-50 disabled:opacity-50"
            >
              {t('status.retry')}
            </button>
          </div>
        )}

        {data && (
          <>
            <p
              className={`text-sm font-medium ${data.status === 'ok' ? 'text-green-700' : 'text-amber-700'}`}
            >
              {data.status === 'ok' ? t('status.ok') : t('status.degraded')}
            </p>
            <ul className="mt-3 divide-y divide-slate-100 text-sm">
              {Object.entries(data.checks).map(([name, check]) => (
                <li key={name} className="flex items-center justify-between py-2">
                  <span>{CHECK_LABELS[name] ? t(CHECK_LABELS[name]) : name}</span>
                  <span className={check.status === 'up' ? 'text-green-700' : 'text-red-700'}>
                    {check.status === 'up' ? t('status.up') : t('status.down')}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-slate-500">
              {data.service} v{data.version} · {data.environment}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
